/**
 * Validation utilities for Clone Table feature.
 */

import type { DatabaseEngineId } from '../../../core/engines/engineTypes';

/**
 * Validate a table name
 */
export function validateTableName(
  name: string,
  engine: DatabaseEngineId
): string | undefined {
  if (!name.trim()) {
    return 'Table name is required';
  }

  // Check length based on engine
  const maxLength = engine === 'pgsql' ? 63 : 128;
  if (name.length > maxLength) {
    return `Table name is too long (max ${maxLength} characters)`;
  }

  // Basic identifier validation - alphanumeric, underscore, no leading numbers
  // SQLite is more permissive but we'll use the same rules for consistency
  if (!/^[a-zA-Z_][a-zA-Z0-9_#]*$/.test(name)) {
    return 'Invalid table name. Use letters, numbers, underscores, and # only (cannot start with a number).';
  }

  return undefined;
}

/**
 * Validate a schema name
 */
export function validateSchemaName(
  schema: string,
  engine: DatabaseEngineId
): string | undefined {
  // SQLite doesn't have schemas
  if (engine === 'sqlite') {
    return undefined;
  }

  if (!schema.trim()) {
    return 'Schema name is required';
  }

  // Check length
  const maxLength = engine === 'pgsql' ? 63 : 128;
  if (schema.length > maxLength) {
    return `Schema name is too long (max ${maxLength} characters)`;
  }

  // Basic identifier validation
  if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(schema)) {
    return 'Invalid schema name. Use letters, numbers, and underscores only.';
  }

  return undefined;
}

/**
 * Check if a table name uses reserved keywords (warning only)
 */
export function isReservedTableKeyword(name: string): boolean {
  const reservedKeywords = [
    // SQL standard
    'select',
    'insert',
    'update',
    'delete',
    'from',
    'where',
    'table',
    'index',
    'create',
    'drop',
    'alter',
    'database',
    'schema',
    'view',
    'order',
    'group',
    'by',
    'having',
    'join',
    'left',
    'right',
    'inner',
    'outer',
    'on',
    'and',
    'or',
    'not',
    'null',
    'true',
    'false',
    'primary',
    'key',
    'foreign',
    'references',
    'constraint',
    'unique',
    'check',
    'default',
    'values',
  ];

  return reservedKeywords.includes(name.toLowerCase());
}

/**
 * Validate clone table form values
 */
export function validateCloneTableForm(values: {
  engine: DatabaseEngineId;
  targetTableName: string;
  targetSchema: string;
}): { valid: boolean; errors: Record<string, string | undefined> } {
  const errors: Record<string, string | undefined> = {};

  errors.targetTableName = validateTableName(
    values.targetTableName,
    values.engine
  );
  errors.targetSchema = validateSchemaName(values.targetSchema, values.engine);

  const valid = Object.values(errors).every((e) => e === undefined);
  return { valid, errors };
}
